import axios from "axios";
import Chart from "chart.js/auto";

export default () => ({
  isMetricsLoading: true,
  chartInstances: {},

  dashboardMetrics: {
    operational: { appointments_today: 0, canceled_today: 0 },
    financial: {
      current_month_revenue: 0,
      current_month_expenses: 0,
      last_month_revenue: 0,
      payable_today: 0,
      overdue_payable: 0,
    },
    clients: { new_clients_month: 0 },
    inventory: { critical_stock: [] },
    charts: null,
  },

  async initDashboardMetrics() {
    this.isMetricsLoading = true;
    try {
      const response = await axios.get("/api/client/dashboard");
      this.dashboardMetrics = response.data.data;

      this.$nextTick(() => {
        this.renderCharts();
      });
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      this.isMetricsLoading = false;
    }
  },

  // async initDashboard() {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const authCode = urlParams.get("auth_code");

  //   if (authCode) {
  //     try {
  //       const exchangeResponse = await axios.post("/api/public/exchange-code", {
  //         auth_code: authCode,
  //       });
  //       localStorage.setItem("jwt_token", exchangeResponse.data.access_token);
  //       localStorage.setItem("is_impersonating", "true");
  //       window.history.replaceState(null, null, window.location.pathname);
  //     } catch (error) {
  //       window.location.href = "/client/login";
  //       return;
  //     }
  //   }

  //   const token = localStorage.getItem("jwt_token");
  //   if (!token) {
  //     window.location.href = "/client/login";
  //     return;
  //   }
  //   window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  //   try {
  //     const response = await axios.get("/api/client/auth/me");
  //     const tenantStatus = response.data.tenant_status;
  //     const isExpired = response.data.is_expired;
  //     const isStatusBlocked =
  //       tenantStatus === "PENDING" ||
  //       tenantStatus === "OVERDUE" ||
  //       tenantStatus === "SUSPENDED";

  //     if (isStatusBlocked || isExpired) {
  //       if (window.location.pathname !== "/client/subscription") {
  //         window.location.href = "/client/subscription";
  //         return;
  //       }
  //       this.isLoading = false;
  //       return;
  //     }

  //     this.userData = response.data.user;
  //     this.tenantData = response.data.tenant;
  //     this.userPermissions = response.data.permissions;
  //     this.planData = response.data.plan;

  //     this.isLoading = false;

  //     if (window.location.pathname === "/client/dashboard") {
  //       await this.fetchDashboardMetrics();
  //     }
  //   } catch (error) {
  //     if (error.response && [401, 402].includes(error.response.status)) return;
  //     this.isLoading = false;
  //   }
  // },

  async fetchDashboardMetrics() {
    this.isMetricsLoading = true;
    try {
      const response = await axios.get("/api/client/dashboard");
      this.dashboardMetrics = response.data.data;

      // Delay initialization slightly to ensure Alpine has evaluated x-show
      this.$nextTick(() => {
        this.renderCharts();
      });
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      this.isMetricsLoading = false;
    }
  },

  /**
   * Initializes and renders all Chart.js instances.
   */
  renderCharts() {
    const chartsData = this.dashboardMetrics.charts;
    if (!chartsData) return;

    const safeDestroy = (key) => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
      }
    };

    // Financial Charts - ONLY IF THE PLAN HAS THE FEATURE
    if (this.hasPlanFeature("view_transactions")) {
      // 1. Chart: Revenue & Expenses (6 Months)
      const ctx6Months = document.getElementById("chart6Months");
      if (ctx6Months) {
        safeDestroy("sixMonths");
        this.chartInstances.sixMonths = new Chart(ctx6Months, {
          type: "bar",
          data: {
            labels: chartsData.six_months.labels,
            datasets: [
              {
                label: "Receitas",
                data: chartsData.six_months.revenue,
                backgroundColor: "#10B981",
                borderRadius: 4,
              },
              {
                label: "Despesas",
                data: chartsData.six_months.expenses,
                backgroundColor: "#EF4444",
                borderRadius: 4,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
          },
        });
      }

      // 2. Chart: Current Month Flow
      const ctxMonthFlow = document.getElementById("chartMonthFlow");
      if (ctxMonthFlow) {
        safeDestroy("monthFlow");
        this.chartInstances.monthFlow = new Chart(ctxMonthFlow, {
          type: "doughnut",
          data: {
            labels: ["Receitas", "Despesas"],
            datasets: [
              {
                data: [
                  chartsData.current_month_flow.revenue,
                  chartsData.current_month_flow.expenses,
                ],
                backgroundColor: ["#10B981", "#EF4444"],
                hoverOffset: 4,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "70%",
          },
        });
      }
    }

    // Operational Charts - ALWAYS RENDERED
    // 3. Chart: 7 Days Appointments
    const ctx7Days = document.getElementById("chart7Days");
    if (ctx7Days) {
      safeDestroy("sevenDays");
      this.chartInstances.sevenDays = new Chart(ctx7Days, {
        type: "line",
        data: {
          labels: chartsData.seven_days_appointments.labels,
          datasets: [
            {
              label: "Agendamentos",
              data: chartsData.seven_days_appointments.data,
              borderColor: "#3B82F6",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
        },
      });
    }

    // 4. Chart: Today's Status Distribution
    const ctxTodayStatus = document.getElementById("chartTodayStatus");
    if (ctxTodayStatus) {
      safeDestroy("todayStatus");
      const hasData =
        chartsData.today_status.data.reduce((a, b) => a + b, 0) > 0;

      this.chartInstances.todayStatus = new Chart(ctxTodayStatus, {
        type: "pie",
        data: {
          labels: hasData
            ? chartsData.today_status.labels
            : ["Nenhum agendamento"],
          datasets: [
            {
              data: hasData ? chartsData.today_status.data : [1],
              backgroundColor: hasData
                ? ["#3B82F6", "#10B981", "#EF4444", "#F59E0B", "#8B5CF6"]
                : ["#E5E7EB"],
            },
          ],
        },
        options: { responsive: true, maintainAspectRatio: false },
      });
    }
  },

  formatCurrency(value) {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  },

  hasPermission(permission) {
    return this.userPermissions.includes(permission);
  },

  hasPlanFeature(feature) {
    return this.planData?.features?.includes(feature) || false;
  },

  hasRole(roleName) {
    return this.userRoles.includes(roleName);
  },

  async logout() {
    try {
      await axios.post("/api/client/auth/logout");
    } catch (error) {
      console.warn("Logout failed, clearing session.");
    } finally {
      // Clear Sentry user context to prevent mixing sessions
      Sentry.setUser(null);

      localStorage.removeItem("jwt_token");
      localStorage.removeItem("is_impersonating");
      window.location.href = "/client/login";
    }
  },
});
